import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Input,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@tonic/shared/components/ui';
import { Search, MoreVertical, Plus } from 'lucide-react';
import { DataTableWrapper } from '@/features/shared/components/data-display/DataTableWrapper';
import { mockRefillContainers } from './mock-data';

const columns = [
  {
    accessorKey: 'sku',
    header: 'SKU',
    cell: ({ row }) => (
      <Link
        to={`/admin/re-commerce/refill/containers/${row.original.id}`}
        className="text-primary hover:underline"
      >
        {row.original.sku}
      </Link>
    )
  },
  {
    accessorKey: 'name',
    header: 'Name'
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <Badge variant="outline">
        {row.original.type}
      </Badge>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={getStatusVariant(row.original.status)}>
        {row.original.status}
      </Badge>
    )
  },
  {
    accessorKey: 'condition',
    header: 'Condition',
    cell: ({ row }) => (
      <Badge variant={getConditionVariant(row.original.condition)}>
        {row.original.condition}
      </Badge>
    )
  },
  {
    accessorKey: 'currentCustomer',
    header: 'Current Customer',
    cell: ({ row }) => row.original.currentCustomer ? (
      <div>
        <div className="font-medium">{row.original.currentCustomer.name}</div>
        <div className="text-sm text-muted-foreground">{row.original.currentCustomer.email}</div>
      </div>
    ) : (
      <span className="text-muted-foreground">-</span>
    )
  },
  {
    accessorKey: 'location',
    header: 'Location'
  },
  {
    accessorKey: 'nextRefill',
    header: 'Next Refill',
    cell: ({ row }) => row.original.nextRefill ? (
      new Date(row.original.nextRefill).toLocaleDateString()
    ) : (
      <span className="text-muted-foreground">-</span>
    )
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem>
            <Link to={`/admin/re-commerce/refill/containers/${row.original.id}`}>
              View Details
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem>Schedule Refill</DropdownMenuItem>
          <DropdownMenuItem>Update Status</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
];

function getStatusVariant(status) {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'default';
    case 'in_use':
      return 'secondary';
    case 'maintenance':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getConditionVariant(condition) {
  switch (condition?.toLowerCase()) {
    case 'excellent':
      return 'default';
    case 'good':
      return 'secondary';
    case 'fair':
      return 'outline';
    case 'poor':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function RefillContainersPage() {
  const [filters, setFilters] = React.useState({
    search: '',
    status: 'all',
    type: 'all'
  });

  const containers = React.useMemo(() => {
    let filteredContainers = [...mockRefillContainers];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredContainers = filteredContainers.filter(container =>
        container.name.toLowerCase().includes(searchLower) ||
        container.sku.toLowerCase().includes(searchLower) ||
        container.currentCustomer?.name.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      filteredContainers = filteredContainers.filter(container =>
        container.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    if (filters.type !== 'all') {
      filteredContainers = filteredContainers.filter(container =>
        container.type.toLowerCase() === filters.type.toLowerCase()
      );
    }

    return filteredContainers;
  }, [filters]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Refill Containers</h1>
          <p className="text-sm text-muted-foreground">
            Manage refillable containers and track their status
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Container
        </Button>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="relative flex-1 w-full md:max-w-sm">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-muted-foreground" />
              </div>
              <Input
                placeholder="Search containers..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-9 w-full"
              />
            </div>

            <div className="flex gap-4 items-center w-full md:w-auto">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="in_use">In Use</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.type}
                onValueChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Food Storage">Food Storage</SelectItem>
                  <SelectItem value="Beverage">Beverage</SelectItem>
                  <SelectItem value="Dry Food">Dry Food</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <DataTableWrapper
            columns={columns}
            data={containers}
            emptyMessage="No containers found"
            loadingMessage="Loading containers..."
          />
        </CardContent>
      </Card>
    </div>
  );
} 
